/**
 * FloatingInfoBanner — Session-scoped floating info banner.
 *
 * Behavior:
 *  - First appearance: 10 minutes after session start
 *  - Then: every 30 minutes
 *  - Max 3 appearances per session
 *  - Auto-dismiss after 12s
 *  - Never repeats a fact within the same session
 *  - Not triggered by route change (timer only)
 *
 * Persistence:
 *  - localStorage key `perform_banner_interactions`
 *    { dismissed: number, liked: number, disliked: number, shownIds: number[] }
 *
 * Accessibility:
 *  - Respects `prefers-reduced-motion` (no slide animation; auto-dismiss kept)
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, X, ThumbsUp, ThumbsDown } from "lucide-react";
import { DAILY_TIPS } from "@/lib/staticContent";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "perform_banner_interactions";
const FIRST_DELAY_MS = 10 * 60 * 1000; // 10 min
const REPEAT_DELAY_MS = 30 * 60 * 1000; // 30 min
const VISIBLE_MS = 12_000;
const MAX_PER_SESSION = 3;

interface BannerInteractions {
  dismissed: number;
  liked: number;
  disliked: number;
  shownIds: number[];
}

const DEFAULT_STATE: BannerInteractions = {
  dismissed: 0,
  liked: 0,
  disliked: 0,
  shownIds: [],
};

function readState(): BannerInteractions {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<BannerInteractions>;
    return {
      dismissed: parsed.dismissed ?? 0,
      liked: parsed.liked ?? 0,
      disliked: parsed.disliked ?? 0,
      shownIds: Array.isArray(parsed.shownIds) ? parsed.shownIds : [],
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function writeState(next: BannerInteractions) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Session-scoped tracker (in-memory, resets on tab close). */
const SESSION = {
  shownInSession: new Set<number>(),
  appearances: 0,
};

export default function FloatingInfoBanner() {
  const [open, setOpen] = useState(false);
  const [tipIndex, setTipIndex] = useState<number | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const scheduleTimerRef = useRef<number | null>(null);
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);

  const pickNextTip = (): number | null => {
    const persisted = readState();
    const seen = new Set<number>([
      ...SESSION.shownInSession,
      ...persisted.shownIds,
    ]);
    const candidates = DAILY_TIPS
      .map((_, i) => i)
      .filter((i) => !seen.has(i));
    // Fallback: if everything seen historically, only block within session
    const pool =
      candidates.length > 0
        ? candidates
        : DAILY_TIPS.map((_, i) => i).filter((i) => !SESSION.shownInSession.has(i));
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const showBanner = () => {
    if (SESSION.appearances >= MAX_PER_SESSION) return;
    const idx = pickNextTip();
    if (idx == null) return;

    SESSION.shownInSession.add(idx);
    SESSION.appearances += 1;

    const persisted = readState();
    persisted.shownIds = Array.from(
      new Set([...persisted.shownIds, idx])
    ).slice(-200); // cap historical list
    writeState(persisted);

    setTipIndex(idx);
    setOpen(true);

    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      scheduleNext();
    }, VISIBLE_MS);
  };

  const scheduleNext = () => {
    if (scheduleTimerRef.current) window.clearTimeout(scheduleTimerRef.current);
    if (SESSION.appearances >= MAX_PER_SESSION) return;
    scheduleTimerRef.current = window.setTimeout(showBanner, REPEAT_DELAY_MS);
  };

  // Initial mount: schedule first appearance at +10min
  useEffect(() => {
    if (SESSION.appearances >= MAX_PER_SESSION) return;
    if (scheduleTimerRef.current) window.clearTimeout(scheduleTimerRef.current);
    scheduleTimerRef.current = window.setTimeout(showBanner, FIRST_DELAY_MS);
    return () => {
      if (scheduleTimerRef.current) window.clearTimeout(scheduleTimerRef.current);
      if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = (interaction: "dismissed" | "liked" | "disliked") => {
    const persisted = readState();
    persisted[interaction] = (persisted[interaction] ?? 0) + 1;
    writeState(persisted);
    setOpen(false);
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    scheduleNext();
  };

  const tipText = tipIndex != null ? DAILY_TIPS[tipIndex] : null;

  return (
    <AnimatePresence>
      {open && tipText && (
        <motion.div
          key="floating-info-banner"
          initial={
            reducedMotion
              ? { opacity: 0 }
              : { opacity: 0, y: 24, scale: 0.96 }
          }
          animate={
            reducedMotion
              ? { opacity: 1 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          exit={
            reducedMotion
              ? { opacity: 0 }
              : { opacity: 0, y: 12, scale: 0.96 }
          }
          transition={{ duration: 0.25, ease: "easeOut" }}
          role="status"
          aria-live="polite"
          className={cn(
            "fixed z-[60] card-glass border border-primary/30 rounded-xl p-3 shadow-lg",
            "pointer-events-auto"
          )}
          style={{
            bottom:
              "calc(80px + env(safe-area-inset-bottom, 0px) + var(--mobile-dock-h, 0px))",
            right: 16,
            width: "min(340px, calc(100vw - 32px))",
          }}
        >
          <div className="flex items-start gap-2.5">
            <div className="shrink-0 h-8 w-8 rounded-lg bg-primary/15 flex items-center justify-center">
              <Brain className="h-4 w-4 text-primary" aria-hidden="true" />
            </div>
            <p
              className="flex-1 text-xs leading-snug text-foreground line-clamp-2"
              title={tipText}
            >
              {tipText}
            </p>
            <button
              type="button"
              onClick={() => close("dismissed")}
              className="shrink-0 -mr-1 -mt-1 p-1 text-muted-foreground hover:text-foreground transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
              aria-label="Fermer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex items-center justify-between mt-2.5 gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => close("liked")}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                aria-label="J'aime ce conseil"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => close("disliked")}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                aria-label="Pas pour moi"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* 12s progress bar */}
            <div className="flex-1 h-1 rounded-full bg-muted/40 overflow-hidden">
              <motion.div
                key={tipIndex}
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: VISIBLE_MS / 1000, ease: "linear" }}
                className="h-full bg-primary/60"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
